import { Button, Textarea } from "@repo/ui";
import { Loader2, Mic, MicOff, Send } from "lucide-react";
import { useEffect, useRef } from "react";
import { RecordingIndicator } from "./RecordingIndicator";

interface ChatInputAreaProps {
  isReadOnly: boolean;
  isInterviewFinished: boolean;
  isSubmitting: boolean;
  isRecording: boolean;
  isTranscribing: boolean;
  isSupported: boolean;
  isMobile: boolean;
  isMicBusy: boolean;
  currentInput: string;
  audioError: string | null;
  audioVolume: number;
  recordingStartTime: number | null;
  onInputChange: (value: string, addedChars: number) => void;
  onPaste: () => void;
  onSend: () => void;
  onMicClick: () => void;
  onFinish: () => void;
  isTyping: boolean;
}

export function ChatInputArea({
  isReadOnly,
  isInterviewFinished,
  isSubmitting,
  isRecording,
  isTranscribing,
  isSupported,
  isMobile,
  isMicBusy,
  currentInput,
  audioError,
  audioVolume,
  recordingStartTime,
  onInputChange,
  onPaste,
  onSend,
  onMicClick,
  onFinish,
  isTyping,
}: ChatInputAreaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus automatique sur la zone de texte après la réponse du chatbot (desktop uniquement)
  useEffect(() => {
    if (!isTyping && !isSubmitting && !isReadOnly && !isInterviewFinished) {
      if (!isMobile) textareaRef.current?.focus();
    }
  }, [isTyping, isSubmitting, isReadOnly, isInterviewFinished, isMobile]);

  return (
    <div className="border-t px-3 pt-1 pb-0 sm:px-6 sm:py-4">
      {isReadOnly ? (
        <div className="text-center text-sm text-muted-foreground py-4">
          L'entretien est terminé. Vous pouvez consulter vos réponses ci-dessus.
        </div>
      ) : isInterviewFinished ? (
        <Button
          onClick={onFinish}
          disabled={isSubmitting}
          className="w-full"
          size="lg"
        >
          {isSubmitting ? "Finalisation..." : "Terminer l'entretien"}
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <RecordingIndicator
                isRecording={isRecording}
                volume={audioVolume}
                startTime={recordingStartTime}
              />
              <Textarea
                ref={textareaRef}
                value={currentInput}
                onChange={(e) => {
                  const newValue = e.target.value || "";
                  const added = Math.max(0, newValue.length - currentInput.length);
                  onInputChange(newValue, added);
                }}
                onPaste={onPaste}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSend();
                  }
                }}
                placeholder={
                  isMobile
                    ? "Écrivez votre réponse ici..."
                    : "Écrivez votre réponse ici... (Entrée pour envoyer, Shift+Entrée pour nouvelle ligne)"
                }
                className={`min-h-24 sm:min-h-25 resize-none w-full ${isRecording ? "pt-8" : ""}`}
                disabled={isSubmitting || isRecording || isTranscribing}
              />
              {isTranscribing && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center gap-2 rounded-surface z-10">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">
                    Transcription en cours...
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {isSupported && (
                <Button
                  onClick={onMicClick}
                  disabled={isMicBusy}
                  size="icon"
                  variant={isRecording ? "destructive" : "outline"}
                  className={`h-10 w-10 sm:h-12 sm:w-12 ${isRecording ? "animate-pulse" : ""}`}
                  title={
                    isRecording
                      ? "Arrêter l'enregistrement"
                      : "Dicter ma réponse"
                  }
                >
                  {isTranscribing ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="h-5 w-5" />
                  ) : (
                    <Mic className="h-5 w-5" />
                  )}
                </Button>
              )}
              <Button
                onClick={onSend}
                disabled={
                  !currentInput.trim() ||
                  isSubmitting ||
                  isRecording ||
                  isTranscribing
                }
                size="icon"
                className={`w-10 sm:w-12 ${isSupported ? "h-10 sm:h-11" : "h-16 sm:h-25"}`}
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
          </div>
          {audioError && (
            <p className="text-sm text-destructive">{audioError}</p>
          )}
        </div>
      )}
    </div>
  );
}
